/**
 * 通用请求方法
 * @module request
 * @example
 *
 * // 基础用法
 * request({
 *   url: '<url>
 * })
 *
 * // 处理警告
 *
 */
import axios, { instance } from './axios';
import { uuid, trim } from './util';
import {injectSessionKey, injectToken} from '@/util/user';
import {MessageBox} from 'element-ui';
import * as appUtils from '@/util/appUtils';
import {MESSAGE_OPERATION_CONTINUE} from '@/util/common-constant';
import Vue from 'vue';
import XMessageDialog from '@/component/x-message-dialog/x-message-dialog.vue';
const XMessageDialogConstructor = Vue.extend(XMessageDialog);

const LOCAL_REQUEST_ID = 'Local-Request-Id';
const RequestCache = {};
// 默认配置
const defaultConfig = {
  // 显示loading
  showLoading: true,
  // 接口调过警告
  skipWarn: false,
  // 清除字段值两旁的空格
  trim: true,
  // 验证响应数据的 success
  validSuccess: false,
  // 显示请求失败错误提示
  failMessage: false,
  // 接口跳过警告时的响应数据回调函数，参数 response.data
  skipWarnCallback: null,
  // 跳过接口的 Promise Executor 函数，参数 resolve, reject
  skipWarnExecutor: null,
  // 跳过接口的Promise finally 回调
  skipWarnFinally: null,
  // 警告响应code编码值
  skipWarnResponseCode: 508,
  // 响应返回原始对象
  originResponse: false,
  // 支持用户自动取消
  cancelable: false,
  // 显示取消按钮时间
  cancelVisibleTime: 10 * 1000,
  // 取消提示组件默认配置
  cancelNotifyConfig: null,
  // 消息提示采用Notification组件
  notify: false,
  // 取消请求时，真实取消发起的请求，设置为false，不会真正取消请求，只是关闭loading
  doCancelRequest: true,
  // 请求超时时间
  timeout: 2 * 60 * 1000
};

const defaultLoadingOptions = {
  spinner: 'el-icon-loading',
  fullscreen: true,
  background: 'rgba(255, 255, 255, 0)',
  customClass: 'custom-loading'
};
let confirmShowing = false;

function isSuccess() {
  return this.code === 0 || this.code === '0';
}

function isFail() {
  return this.code !== 0 && this.code !== '0';
}
instance.interceptors.request.use(config => {
  const headers = config.headers;
  injectToken({headers});
  injectSessionKey({headers})
  return config;
});
instance.interceptors.response.use(res => {
  // 从请求头配置拿到请求的标识id，从缓存中找回对应的请求配置参数
  const requestId = res.config.headers[LOCAL_REQUEST_ID];
  const requestCache = RequestCache[requestId];
  // 增加判断请求是否成功的方法
  if (typeof res.data === 'object') {
    res.data.isSuccess = isSuccess;
    res.data.isFail = isFail;
  }
  // 接口响应警告信息时
  if (requestCache && Number.parseInt(res.data?.code) === requestCache.config.skipWarnResponseCode) {
    res.data.promise = showMessage({title: '提示信息', messageData: res.data.data}).then(({type}) => {
      if (type === MESSAGE_OPERATION_CONTINUE) {
        requestCache.config.skipWarn = true;
        // 已经处理了警告，确定重新发起跳过警告请求
        return service(requestCache.options, requestCache.config);
      }
    });
    /*const {skipWarnCallback, skipWarnExecutor, skipWarnFinally} = requestCache.config;
    // 如果设置了跳过警告，或未设置警告处理函数，不做处理
    if (!skipWarnExecutor) return res;
    skipWarnCallback && skipWarnCallback(res.data);
    // 记录处理警告的处理函数
    res.data.promise = new Promise(skipWarnExecutor).then(_ => {
      requestCache.config.skipWarn = true;
      // 已经处理了警告，确定重新发起跳过警告请求
      return service(requestCache.options, requestCache.config);
    });
    res.data.promise.catch(e => e)
      .finally(_ => {
        skipWarnFinally && skipWarnFinally();
      });*/
  }
  // 登录失效，提示用户重新登录
  if (res.data.code === 1001) {
    confirmShowing = true;
    MessageBox.confirm(res.data.msg, "提示", {
      type: 'warning',
      confirmButtonText: '确定'
    }).then(() => {
      window.location.replace(window.location.origin + '/login');
    }).finally(() => {
      confirmShowing = false;
    });
  }
  // 删除缓存
  delete RequestCache[requestId];
  return res;
});

/**
 * 发起请求函数
 * @param {object} options axios配置参数对象
 * @param {object} config 针对本次请求的功能设置
 * @param {boolean} [config.showLoading=true] 显示loading
 * @param {boolean} [config.skipWarn=false] 跳过接口警告
 * @param {boolean} [config.trim=true] 清除字段值两旁的空格
 * @param {boolean} [config.validSuccess=false] 验证相应数据的 success
 * @param {boolean} [config.failMessage=true] 显示请求失败错误提示
 * @param {function} [config.skipWarnCallback] 接口跳过警告时的相应数据回调函数，参数 response.data
 * @param {function} [config.skipWarnExecutor] 接口跳过接口的 Promise Executor 函数，参数 resolve, reject
 * @param {function} [config.skipWarnFinally] 接口跳过接口的Promise finally 回调
 * @param {number} [config.skipWarnResponseCode=7] 警告相应code编码值
 * @param {boolean} [config.originResponse=true] 响应返回原始对象
 * @param {boolean} [config.cancelable=true] 开启请求取消功能
 * @param {number} [config.cancelVisibleTime=10x1000] 显示取消提示时间
 * @param {object} [config.cancelNotifyConfig] 取消提示组件配置
 * @param {boolean} [config.notify] 消息提示采用Notification组件
 * @param {number} [config.timeout] 接口超时时间
 * @param {boolean} [config.doCancelRequest] 取消请求时，真实取消发起的请求，设置为false，不会真正取消请求，只是关闭loading
 * @return {Promise}
 */
function service(options = {}, config = {}) {
  config = {...defaultConfig, ...config};

  options.settings = {
    loading: config.showLoading ? {...defaultLoadingOptions, ...config.showLoading} : false,
    validSuccess: config.validSuccess,
    failMessage: config.failMessage,
    originResponse: config.originResponse,
    cancelable: config.cancelable,
    cancelVisibleTime: config.cancelVisibleTime,
    cancelNotifyConfig: config.cancelNotifyConfig,
    doCancelRequest: config.doCancelRequest,
    notify: config.notify,
    ...(options.settings || {})
  };
  if (options.data) {
    options.data.skipWarn = config.skipWarn;
    options.data = config.trim ? trim(options.data) : options.data;
  }
  if (options.params) {
    options.params.skipWarn = config.skipWarn;
    options.params = config.trim ? trim(options.params) : options.params;
    // get请求，json字符串需要转移
    /*for (let key in options.params) {
      const value = options.params[key];
      if (isString(value) && (value.startsWith('{') || value.startsWith('['))) {
        options.params[key] = encodeURIComponent(value);
      }
    }*/
  }

  const id = uuid(false);
  options.headers = options.headers || {};
  options.headers[LOCAL_REQUEST_ID] = id;
  RequestCache[id] = {
    options: {...options},
    config
  };

  if (config.timeout) {
    options.timeout = config.timeout;
  }
  return axios(options);
}

export default service;

/**
 * 设置默认配置
 * @function setConfig
 * @param {object} config 配置对象
 */
export function setConfig(config = {}) {
  Object.assign(defaultConfig, config);
}

/**
 * 显示x-message-box
 * @param title 标题
 * @param type 类型
 * @param message 消息
 * @param messageData 消息列表数据，如果传入了messageData, type和message就会失效，如果没有传入messageData，就会根据type和message组装messageData
 * @return Promise
 */
export const showMessage = ({title, type, message, messageData}) => {
  if (!Array.isArray(messageData) && appUtils.isNotEmpty(message)) {
    messageData = [
      {
        checkType: type,
        checkMsg: message
      }
    ];
  }
  // 没有信息
  if (!Array.isArray(messageData) || messageData.length < 1) {
    return Promise.resolve();
  }
  let resolveFn = null;
  let rejectFn = null;
  const promise = new Promise((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  let flag = false;
  const vm = new XMessageDialogConstructor({
    el: document.createElement('div'),
    propsData: {
      messageData,
      title,
      visible: true,
      handleFn: (args) => {
        // 主动调用vm.visible = false会导致x-message-dialog的@close再次执行
        if (flag) {
          return;
        }
        flag = true;
        vm.visible = false;
        vm.$destroy();
        const { type } = args;
        if (type === MESSAGE_OPERATION_CONTINUE) {
          resolveFn(args);
          return;
        }
        const error = new NonsenseError('user not continue');
        error.messageArgs = args;
        rejectFn(error);
      }
    }
  });
  return promise;
};

export {
  LOCAL_REQUEST_ID
};
